// src/hooks/api/useNotificationsManagement.js
import { useState, useCallback } from 'react';
import { notificationsAPI } from '../services/notifications';
import { useApi } from '../../../hooks/api/useApi';

export function useNotificationsManagement() {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    hasMore: false
  });
  const { loading, error, execute, clearError } = useApi();

  // Fetch notifications with pagination and filtering
  const fetchNotifications = useCallback(async (params = {}) => {
    const {
      include_read = true,
      limit = pagination.pageSize,
      offset = pagination.page * pagination.pageSize,
      ...otherParams
    } = params;

    const result = await execute(() => 
      notificationsAPI.getNotifications({
        include_read,
        limit,
        offset,
        ...otherParams
      })
    );

    if (result.success) {
      const data = result.data;
      setNotifications(data.notifications || []);
      setPagination(prev => ({
        ...prev,
        total: data.total_count || 0,
        hasMore: data.has_more || false
      }));
    }
    return result;
  }, [execute, pagination.pageSize, pagination.page]);

  // Load more notifications (for infinite scroll)
  const loadMoreNotifications = useCallback(async () => {
    if (!pagination.hasMore || loading) return;

    const nextPage = pagination.page + 1;
    const result = await execute(() => 
      notificationsAPI.getNotifications({
        include_read: true,
        limit: pagination.pageSize,
        offset: nextPage * pagination.pageSize
      })
    );

    if (result.success) {
      const data = result.data;
      setNotifications(prev => [...prev, ...(data.notifications || [])]);
      setPagination(prev => ({
        ...prev,
        page: nextPage,
        hasMore: data.has_more || false
      }));
    }
    return result;
  }, [execute, pagination, loading]);

  // Create a single notification
  const createNotification = useCallback(async (notificationData) => {
    const result = await execute(() => notificationsAPI.createNotification(notificationData));
    if (result.success) {
      // Refresh notifications to show the new one
      await fetchNotifications();
    }
    return result;
  }, [execute, fetchNotifications]);

  // Create bulk notifications
  const createBulkNotifications = useCallback(async (bulkData) => {
    const result = await execute(() => notificationsAPI.createBulkNotifications(bulkData));
    if (result.success) {
      // Refresh notifications to show the new ones
      await fetchNotifications();
    }
    return result;
  }, [execute, fetchNotifications]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    const result = await execute(() => notificationsAPI.markAsRead(notificationId));
    if (result.success) {
      setNotifications(prev => 
        prev.map(notification =>
          notification.notification_id === notificationId
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        )
      );
    }
    return result;
  }, [execute]);

  // Mark multiple notifications as read
  const markMultipleAsRead = useCallback(async (notificationIds) => {
    const results = await Promise.allSettled(
      notificationIds.map(id => execute(() => notificationsAPI.markAsRead(id)))
    );

    const successfulIds = notificationIds.filter((_, index) => 
      results[index].status === 'fulfilled' && results[index].value.success
    );

    if (successfulIds.length > 0) {
      setNotifications(prev => 
        prev.map(notification =>
          successfulIds.includes(notification.notification_id)
            ? { ...notification, is_read: true, read_at: new Date().toISOString() }
            : notification
        )
      );
    }

    return {
      success: successfulIds.length > 0,
      successCount: successfulIds.length,
      totalCount: notificationIds.length
    };
  }, [execute]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter(n => !n.is_read);
    if (unreadNotifications.length === 0) return { success: true, count: 0 };

    return await markMultipleAsRead(
      unreadNotifications.map(n => n.notification_id)
    );
  }, [notifications, markMultipleAsRead]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    const result = await execute(() => notificationsAPI.deleteNotification(notificationId));
    if (result.success) {
      setNotifications(prev => 
        prev.filter(notification => notification.notification_id !== notificationId)
      );
    }
    return result;
  }, [execute]);

  // Delete multiple notifications
  const deleteMultipleNotifications = useCallback(async (notificationIds) => {
    const results = await Promise.allSettled(
      notificationIds.map(id => execute(() => notificationsAPI.deleteNotification(id)))
    );

    const successfulIds = notificationIds.filter((_, index) => 
      results[index].status === 'fulfilled' && results[index].value.success
    );

    if (successfulIds.length > 0) {
      setNotifications(prev => 
        prev.filter(notification => !successfulIds.includes(notification.notification_id))
      );
    }

    return {
      success: successfulIds.length > 0,
      successCount: successfulIds.length,
      totalCount: notificationIds.length
    };
  }, [execute]);

  // Get notification statistics
  const fetchStats = useCallback(async (daysBack = 30) => {
    const result = await execute(() => notificationsAPI.getStats({ days_back: daysBack }));
    if (result.success) {
      setStats(result.data);
    }
    return result;
  }, [execute]);

  // Cleanup expired notifications (admin only)
  const cleanupExpired = useCallback(async () => {
    const result = await execute(() => notificationsAPI.cleanup());
    if (result.success) {
      // Refresh notifications after cleanup
      await fetchNotifications();
    }
    return result;
  }, [execute, fetchNotifications]);

  // Filter notifications by type
  const filterByType = useCallback((type) => {
    return notifications.filter(notification => notification.notification_type === type);
  }, [notifications]);

  // Filter notifications by priority
  const filterByPriority = useCallback((priority) => {
    return notifications.filter(notification => notification.priority === priority);
  }, [notifications]);

  // Get unread notifications
  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(notification => !notification.is_read);
  }, [notifications]);

  // Get notifications by date range
  const filterByDateRange = useCallback((startDate, endDate) => {
    return notifications.filter(notification => {
      const createdAt = new Date(notification.created_at);
      return createdAt >= startDate && createdAt <= endDate;
    });
  }, [notifications]);

  // Reset pagination and filters
  const resetFilters = useCallback(() => {
    setPagination(prev => ({ ...prev, page: 0 }));
    setNotifications([]);
  }, []);

  return {
    // State
    notifications,
    stats,
    pagination,
    loading,
    error,

    // Actions
    fetchNotifications,
    loadMoreNotifications,
    createNotification,
    createBulkNotifications,
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    deleteMultipleNotifications,
    fetchStats,
    cleanupExpired,

    // Utilities
    filterByType,
    filterByPriority,
    getUnreadNotifications,
    filterByDateRange,
    resetFilters,
    clearError,

    // Computed values
    unreadCount: notifications.filter(n => !n.is_read).length,
    totalCount: notifications.length,
    hasMore: pagination.hasMore,
  };
}